import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';

// Un fotograma a 60 Hz. El corte se adelanta ese pelo al endMs porque 16 ms antes es
// invisible, mientras que desbordarse deja ver justo el fotograma que la entrenadora recortó.
const CUT_LOOKAHEAD_MS = 16;

// HAVE_METADATA: a partir de aquí `duration` es fiable y el navegador acepta el `currentTime`.
const HAVE_METADATA = 1;

// ms -> segundos. Descarta null/NaN/negativos para que una marca sucia del backend no acabe
// en `currentTime = NaN`, que en Chrome lanza y deja el video en negro sin más pistas.
const toSeconds = (ms) => {
  const value = Number(ms);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value / 1000;
};

/**
 * OTRIX Play — reproductor del video recortado de las prácticas de descanso.
 *
 * El recorte es NO DESTRUCTIVO: no se recodifica nada, solo se pilota `currentTime` sobre el
 * archivo original. En el servidor no hay ffmpeg, y sobre todo reajustar el recorte tiene que
 * costar cambiar un entero, no volver a subir 200 MB desde el móvil de la entrenadora.
 *
 * `startMs`/`endMs` en NULL = comportamiento de siempre (el archivo entero).
 */
function TrimmedVideo({
  src,
  startMs = null,
  endMs = null,
  muted = true,
  loop = true,
  autoPlay = true,
  controls = false,
  objectFit = 'cover',
  onEnded = null,
  onError = null,
  onPlayBlocked = null,
  onProgress = null,
  sx = {}
}) {
  const videoRef = useRef(null);
  const endedFiredRef = useRef(false);
  const lastWholeSecondRef = useRef(null);

  // Los callbacks viven en una ref: si entraran como dependencias del efecto del rAF, cada
  // render del padre con props inline (onProgress={() => ...}) cancelaría y reprogramaría el
  // bucle, y el corte perdería precisión justo en el frame que importa.
  const handlersRef = useRef({ onEnded, onError, onPlayBlocked, onProgress });
  useEffect(() => {
    handlersRef.current = { onEnded, onError, onPlayBlocked, onProgress };
  });

  const startSeconds = toSeconds(startMs) ?? 0;
  const endSeconds = toSeconds(endMs);
  // Booleano estable en vez de la función: así el efecto del rAF no depende de la identidad
  // del callback y las tarjetas decorativas (sin onProgress ni endMs) no abren bucle ninguno.
  const wantsProgress = typeof onProgress === 'function';

  // startMs más allá del final (marcas guardadas contra OTRO archivo, o video reemplazado sin
  // limpiar el recorte): se ignora y se arranca en 0. Un seek imposible deja el <video>
  // congelado en negro en Safari, sin evento de error que permita reaccionar.
  const startFor = useCallback(
    (el) => (Number.isFinite(el.duration) && startSeconds >= el.duration ? 0 : startSeconds),
    [startSeconds]
  );

  const seekToStart = useCallback(
    (el) => {
      const target = startFor(el);
      if (target > 0) el.currentTime = target;
    },
    [startFor]
  );

  const tryPlay = useCallback((el) => {
    const started = el.play();
    // play() devuelve promesa en los navegadores modernos. Si la rechazan (política de
    // autoplay, iPhone en bajo consumo) hay que AVISAR al de arriba, nunca "arreglarlo"
    // desmuteando: el autoplay solo está permitido en mudo, quitar el muted haría que el
    // navegador bloquee la reproducción entera en vez de dejarla sonar.
    if (started && typeof started.catch === 'function') {
      started.catch(() => {
        handlersRef.current.onPlayBlocked?.();
      });
    }
  }, []);

  // Arranque limpio ante cualquier cambio de archivo o de marca de entrada. El <video> lleva
  // además `key` con esos mismos valores para que React monte un elemento nuevo y no herede
  // el buffer ni la posición del anterior.
  useEffect(() => {
    const el = videoRef.current;
    endedFiredRef.current = false;
    lastWholeSecondRef.current = null;

    // El seek solo "pega" DESPUÉS de los metadatos: iOS descarta en silencio cualquier
    // currentTime anterior y el video arrancaría desde cero sin avisar. Lo normal es que de
    // esto se encargue onLoadedMetadata; aquí se cubre el caso de que ya estuvieran cargados
    // (archivo en caché), porque entonces el evento ya no vuelve a dispararse.
    if (!el || el.readyState < HAVE_METADATA) return;
    seekToStart(el);
    if (autoPlay) tryPlay(el);
  }, [src, startSeconds, autoPlay, seekToStart, tryPlay]);

  // React no siempre serializa `muted` como atributo en el HTML inicial, y Safari decide si
  // permite el autoplay ANTES de eso: se fija la propiedad a mano para no perder el arranque.
  useEffect(() => {
    const el = videoRef.current;
    if (el) el.muted = muted;
  }, [muted, src]);

  // Corte de salida y progreso, con requestAnimationFrame. Con onTimeUpdate no vale: dispara a
  // ~4 Hz y dejaría hasta 250 ms de desbordamiento en CADA vuelta del bucle, que en un recorte
  // de 6 s se nota como un tirón.
  useEffect(() => {
    const el = videoRef.current;
    // Sin corte de salida y sin onProgress no hay nada que vigilar: no se abre el bucle.
    if (!el || (endSeconds === null && !wantsProgress)) return undefined;

    const cutAtMs = endSeconds === null ? null : endSeconds * 1000 - CUT_LOOKAHEAD_MS;
    let rafId = requestAnimationFrame(function tick() {
      rafId = requestAnimationFrame(tick);
      const currentMs = el.currentTime * 1000;

      if (wantsProgress) {
        // Como mucho una emisión por segundo ENTERO: subir estado a React en cada frame
        // repinta el árbol 60 veces por segundo para mover un contador que solo cambia una
        // vez por segundo.
        const wholeSecond = Math.floor(el.currentTime);
        if (wholeSecond !== lastWholeSecondRef.current) {
          lastWholeSecondRef.current = wholeSecond;
          handlersRef.current.onProgress?.(Math.round(currentMs));
        }
      }

      // En pausa el tiempo no avanza: evaluar el corte también ahí rebobinaría sin parar al
      // usuario que paró el video pasado el endMs con los controles nativos.
      if (cutAtMs === null || el.paused || currentMs < cutAtMs) return;

      if (loop) {
        el.currentTime = startFor(el);
        lastWholeSecondRef.current = null; // la vuelta nueva vuelve a emitir su primer segundo
        return;
      }

      el.pause();
      if (endedFiredRef.current) return;
      endedFiredRef.current = true;
      handlersRef.current.onEnded?.();
    });

    // Sin este cancel el bucle sobrevive al desmontaje y sigue midiendo un <video> muerto.
    return () => cancelAnimationFrame(rafId);
  }, [src, startSeconds, endSeconds, loop, wantsProgress, startFor]);

  const handleLoadedMetadata = () => {
    const el = videoRef.current;
    if (!el) return;
    // Primero el seek y luego play(): al revés se ve un fogonazo del fotograma 0 antes del
    // salto al recorte.
    seekToStart(el);
    if (autoPlay) tryPlay(el);
  };

  const handleNativeEnded = () => {
    const el = videoRef.current;
    if (!el) return;
    // Se llega aquí cuando el archivo se acaba antes del endMs, o cuando no hay recorte de
    // salida. El ciclo se cierra a mano porque el atributo `loop` nativo rebobina a 0 y se
    // saltaría el recorte de ENTRADA.
    if (loop) {
      el.currentTime = startFor(el);
      lastWholeSecondRef.current = null;
      tryPlay(el);
      return;
    }
    if (endedFiredRef.current) return;
    endedFiredRef.current = true;
    handlersRef.current.onEnded?.();
  };

  const handleNativeError = () => {
    // El de arriba necesita poder desbloquearse (imagen de respaldo, saltar el paso): un
    // recuadro negro eterno deja la práctica guiada sin salida.
    handlersRef.current.onError?.();
  };

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', backgroundColor: 'surface.raised', ...sx }}>
      {/* Video demostrativo de la práctica: no hay diálogo ni voz en off que subtitular (la
          instrucción va en el texto del paso), por eso no lleva pista <track>. */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        key={`${src}|${startSeconds}`}
        ref={videoRef}
        src={src}
        controls={controls}
        playsInline
        preload={autoPlay ? 'auto' : 'metadata'}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleNativeEnded}
        onError={handleNativeError}
        style={{ width: '100%', height: '100%', objectFit, display: 'block' }}
      />
    </Box>
  );
}

TrimmedVideo.propTypes = {
  src: PropTypes.string.isRequired,
  /** Marca de entrada en MILISEGUNDOS (un recortador es sub-segundo). NULL = desde el inicio. */
  startMs: PropTypes.number,
  /** Marca de salida en MILISEGUNDOS. NULL = hasta el final del archivo. */
  endMs: PropTypes.number,
  muted: PropTypes.bool,
  loop: PropTypes.bool,
  autoPlay: PropTypes.bool,
  controls: PropTypes.bool,
  objectFit: PropTypes.string,
  /** Solo con loop=false, y una única vez por reproducción. */
  onEnded: PropTypes.func,
  onError: PropTypes.func,
  /** play() rechazado por la política del navegador: toca ofrecer un botón de reproducir. */
  onPlayBlocked: PropTypes.func,
  /** Recibe la posición ABSOLUTA del archivo en ms; resta startMs si quieres la del recorte. */
  onProgress: PropTypes.func,
  sx: PropTypes.object
};

export default TrimmedVideo;
